import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Users, CreditCard, IndianRupee, Sparkles, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ParticipantLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "event" | "group_purchase";
  entityId: string;
  currentParticipants: number;
  maxFreeParticipants: number;
  paidSlots: number;
  organizerEmail: string;
  participantEmail?: string; // If participant is paying for themselves
  onPaymentComplete?: () => void;
}

const FREE_PARTICIPANT_LIMIT = 10;
const COST_PER_PARTICIPANT = 10; // ₹10 per additional participant

export function ParticipantLimitDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  currentParticipants,
  maxFreeParticipants = FREE_PARTICIPANT_LIMIT,
  paidSlots = 0,
  organizerEmail,
  participantEmail,
  onPaymentComplete,
}: ParticipantLimitDialogProps) {
  const [paymentOption, setPaymentOption] = useState<"organizer" | "participant">("organizer");
  const [slotsToAdd, setSlotsToAdd] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const totalSlots = maxFreeParticipants + paidSlots;
  const remainingFreeSlots = Math.max(0, maxFreeParticipants - currentParticipants);
  const needsPayment = currentParticipants >= totalSlots;
  const isParticipantView = !!participantEmail;

  const totalCost = slotsToAdd * COST_PER_PARTICIPANT;

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      // Create a payment record
      const { data: paymentRecordData, error: insertError } = await supabase
        .from("participant_limit_payments" as any)
        .insert({
          entity_type: entityType,
          entity_id: entityId,
          paid_by_email: isParticipantView && paymentOption === "participant" 
            ? participantEmail 
            : organizerEmail,
          paid_by_type: paymentOption,
          participant_email: isParticipantView ? participantEmail : null,
          extra_participants: slotsToAdd,
          amount_per_participant: COST_PER_PARTICIPANT,
          total_amount: totalCost,
          status: "pending",
        } as any)
        .select()
        .single();

      if (insertError) throw insertError;
      
      const paymentRecord = paymentRecordData as any;
      const paymentRecordId = paymentRecord?.id;
      
      if (!paymentRecordId) throw new Error("Failed to create payment record");

      // Create Razorpay order
      const { data: orderData, error: orderError } = await supabase.functions.invoke(
        "create-razorpay-order",
        {
          body: {
            amount: totalCost * 100, // Razorpay expects paise
            currency: "INR",
            receipt: `participant_slots_${paymentRecord.id}`,
            notes: {
              type: "participant_slots",
              entity_type: entityType,
              entity_id: entityId,
              slots: slotsToAdd,
              payment_id: paymentRecordId,
            },
          },
        }
      );

      if (orderError || !orderData?.order?.id) {
        throw new Error("Failed to create payment order");
      }

      // Load Razorpay checkout
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: totalCost * 100,
        currency: "INR",
        name: "Shakatah",
        description: `${slotsToAdd} Additional Participant Slot${slotsToAdd > 1 ? 's' : ''}`,
        order_id: orderData.order.id,
        handler: async (response: any) => {
          try {
            // Verify payment
            const { error: verifyError } = await supabase.functions.invoke(
              "verify-razorpay-payment",
              {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  payment_type: "participant_slots",
                  payment_record_id: paymentRecordId,
                },
              }
            );

            if (verifyError) throw verifyError;

            // Update the entity with new paid slots
            const table = entityType === "event" ? "gift_reminders" : "group_purchases";
            const { error: updateError } = await supabase
              .from(table as any)
              .update({
                paid_participant_slots: paidSlots + slotsToAdd,
                participant_fee_paid_by: paymentOption,
              } as any)
              .eq("id", entityId);

            if (updateError) throw updateError;

            // Update payment record
            await supabase
              .from("participant_limit_payments" as any)
              .update({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                status: "paid",
                paid_at: new Date().toISOString(),
              } as any)
              .eq("id", paymentRecordId);

            toast.success(`Added ${slotsToAdd} participant slot${slotsToAdd > 1 ? 's' : ''}!`);
            onOpenChange(false);
            onPaymentComplete?.();
          } catch (err) {
            console.error("Payment verification failed:", err);
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          email: isParticipantView && paymentOption === "participant" 
            ? participantEmail 
            : organizerEmail,
        },
        theme: {
          color: "#ec4899",
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to process payment");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-pink-500" />
            Participant Limit Reached
          </DialogTitle>
          <DialogDescription>
            Free tier allows up to {FREE_PARTICIPANT_LIMIT} participants. Add more with our paid option.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Status */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium">Current Participants</p>
              <p className="text-xs text-muted-foreground">
                {remainingFreeSlots > 0 
                  ? `${remainingFreeSlots} free slots remaining` 
                  : `${paidSlots} paid slots active`}
              </p>
            </div>
            <Badge variant={needsPayment ? "destructive" : "secondary"}>
              {currentParticipants} / {totalSlots}
            </Badge>
          </div>

          {/* Pricing Info */}
          <Alert>
            <Sparkles className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-1">
              <IndianRupee className="w-3 h-3" />
              <span className="font-semibold">{COST_PER_PARTICIPANT}</span>
              <span>per additional participant</span>
            </AlertDescription>
          </Alert>

          {/* Slots to Add */}
          <div className="space-y-2">
            <Label>How many slots to add?</Label>
            <div className="flex gap-2">
              {[1, 5, 10, 20].map((num) => (
                <Button
                  key={num}
                  variant={slotsToAdd === num ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSlotsToAdd(num)}
                  className="flex-1"
                >
                  +{num}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Total: <span className="font-semibold">₹{totalCost}</span> for {slotsToAdd} slot{slotsToAdd > 1 ? 's' : ''}
            </p>
          </div>

          {/* Payment Option */}
          {isParticipantView ? (
            <div className="space-y-3">
              <Label>Who pays?</Label>
              <RadioGroup value={paymentOption} onValueChange={(v) => setPaymentOption(v as "organizer" | "participant")}>
                <div className="flex items-start space-x-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="participant" id="participant" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="participant" className="cursor-pointer font-medium">
                      I'll pay (₹{totalCost})
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Pay now to add yourself as a participant
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="organizer" id="organizer-pay" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="organizer-pay" className="cursor-pointer font-medium">
                      Request organizer to pay
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Notify the organizer to add more slots
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-4 h-4 text-pink-600" />
                <span className="font-medium text-sm">Organizer Payment</span>
              </div>
              <p className="text-xs text-muted-foreground">
                You'll be paying ₹{totalCost} to add {slotsToAdd} participant slot{slotsToAdd > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {(isParticipantView && paymentOption === "organizer") ? (
            <Button disabled className="gap-2">
              <Lock className="w-4 h-4" />
              Request Sent to Organizer
            </Button>
          ) : (
            <Button 
              onClick={handlePayment} 
              disabled={isProcessing}
              className="gap-2"
            >
              <CreditCard className="w-4 h-4" />
              {isProcessing ? "Processing..." : `Pay ₹${totalCost}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const FREE_PARTICIPANT_LIMIT_CONST = FREE_PARTICIPANT_LIMIT;
export const COST_PER_PARTICIPANT_CONST = COST_PER_PARTICIPANT;
